import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit3, Trash2, ExternalLink, Filter, Search as SearchIcon, Loader2, AlertTriangle } from 'lucide-react';
import api from "../../../../api/axios"; // Your custom axios instance
import { Link } from "react-router-dom"


const ShowProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOutOfStockOnly, setShowOutOfStockOnly] = useState(false); // New Filter State

  const fetchProducts = async () => {
    try {
      const res = await api.get("all_products");
      if (res.data.success) {
        setProducts(res.data.data);
      }
    } catch (err) {
      console.error("Error loading products:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce produit et toutes ses images ?")) return;
    try {
      const res = await api.delete(`/products/${id}`);
      if (res.data.success) {
        setProducts(prev => prev.filter(p => p.id !== id));
      }
    } catch (err) {
      alert("Erreur lors de la suppression");
    }
  };

  // Logic to count out of stock items
  const outOfStockCount = products.filter(p => p.stock <= 0).length;

  // Combined Filtering Logic
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          p.brand?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStockFilter = showOutOfStockOnly ? p.stock <= 0 : true;
    
    return matchesSearch && matchesStockFilter;
  });

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-7xl mx-auto p-4"
    >
      {/* Header Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-2xl font-black uppercase tracking-tight text-gray-900">Inventaire Produits</h2>
          <p className="text-sm font-bold text-[#8A8A89] mt-1">
            Gérez vos {products.length} articles ELECTRO TITAN
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Search Bar */}
          <div className="relative hidden sm:block">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input 
              type="text" 
              placeholder="Chercher un produit..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-3 bg-white border border-gray-100 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-[#ffaa00] outline-none shadow-sm w-64"
            />
          </div>

          {/* Out of Stock Filter Button */}
          <button 
            onClick={() => setShowOutOfStockOnly(!showOutOfStockOnly)}
            className={`flex items-center gap-2 px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-sm border ${
              showOutOfStockOnly 
                ? 'bg-red-500 border-red-500 text-white' 
                : 'bg-white border-gray-100 text-gray-500 hover:border-red-200'
            }`}
          >
            <AlertTriangle size={16} />
            <span>Rupture ({outOfStockCount})</span>
          </button>

          <button className="p-3 bg-white border border-gray-100 rounded-2xl text-gray-500 hover:text-[#ffaa00] transition-colors shadow-sm">
            <Filter size={20} />
          </button>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/30 border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Loader2 className="animate-spin text-[#ffaa00]" size={40} />
            <p className="text-xs font-black uppercase text-gray-400 tracking-widest">Mise à jour du stock...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="px-6 py-5 text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">Produit</th>
                  <th className="px-6 py-5 text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">Marque</th>
                  <th className="px-6 py-5 text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">Prix Actuel</th>
                  <th className="px-6 py-5 text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">Stock</th>
                  <th className="px-6 py-5 text-[10px] font-black uppercase tracking-widest text-[#8A8A89] text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                <AnimatePresence mode="popLayout">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => (
                      <motion.tr 
                        layout
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, x: -20 }}
                        key={product.id} 
                        className="hover:bg-gray-50/80 transition-colors group"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white p-1 flex-shrink-0 border border-gray-100 shadow-sm overflow-hidden relative">
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}${product.main_image}`} 
                                alt={product.title} 
                                className={`w-full h-full object-contain group-hover:scale-110 transition-transform duration-500 ${product.stock <= 0 ? 'grayscale' : ''}`} 
                              />
                            </div>
                            <div className="flex flex-col">
                               <span className="font-black text-sm text-gray-900 line-clamp-1">{product.title}</span>
                               <span className="text-[10px] font-bold text-gray-400">ID: #{product.id}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-orange-50 text-[#ffaa00] rounded-lg text-[10px] font-black uppercase tracking-wider">
                            {product.brand || 'No Brand'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="font-black text-sm text-gray-900">{product.price} DH</span>
                            {product.old_price > product.price && (
                              <span className="text-[10px] font-bold text-red-500 line-through">
                                {product.old_price} DH
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${product.stock <= 0 ? 'bg-red-600' : product.stock <= 5 ? 'bg-orange-500 animate-pulse' : 'bg-green-500'}`}></div>
                            <span className={`font-bold text-sm ${product.stock <= 0 ? 'text-red-600 font-black' : 'text-gray-700'}`}>
                              {product.stock <= 0 ? 'ÉPUISÉ' : `${product.stock} Unités`}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Link to={"../edit-product/"+product.id}>
                              <button className="p-2.5 bg-white shadow-sm rounded-xl border border-gray-100 text-[#8A8A89] hover:text-[#ffaa00] transition-all">
                                <Edit3 size={18} />
                              </button>
                            </Link>

                            <button 
                              onClick={() => handleDelete(product.id)}
                              className="p-2.5 bg-white shadow-sm rounded-xl border border-gray-100 text-[#8A8A89] hover:text-red-500 transition-all"
                            >
                              <Trash2 size={18} />
                            </button>

                            <Link target='_blank' to={"/product/"+product.slug}>
                              <button className="p-2.5 bg-white shadow-sm rounded-xl border border-gray-100 text-[#8A8A89] hover:text-blue-500 transition-all">
                                <ExternalLink size={18} />
                              </button>
                            </Link>
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-20 text-center">
                        <p className="text-gray-400 font-black uppercase tracking-tighter text-lg">Aucun produit trouvé</p>
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ShowProducts;